import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Calendar, Pin, PinOff, Search } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { PageHeader } from "../components/common/PageHeader";
import { SchoolFilter } from "../components/filters/SchoolFilter";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { useDataFetching } from "../hooks/useDataFetching";
import { eventsAPI, schoolsAPI } from "../api";
import { QUERY_KEYS } from "../utils/constants";
import { formatDateTime } from "../utils/formatters";
import { getSchoolName, handleSchoolId } from "../utils/helpers";
import { Event, ApiDataResponse } from "../types";

/**
 * Featured events: every pinned event in one row, plus a picker to pin more.
 * Pinned events are listed first in the student app's events list and home.
 */
export function PinnedEventsPage() {
  const { data: eventsData, isLoading, error } = useDataFetching(QUERY_KEYS.EVENTS, {
    queryFn: eventsAPI.getAllEvents,
  });
  const { data: schoolsData } = useDataFetching(QUERY_KEYS.SCHOOLS, {
    queryFn: schoolsAPI.getAllSchools,
  });

  const events: Event[] = Array.isArray(eventsData)
    ? eventsData
    : (eventsData as ApiDataResponse<Event[]>)?.data || [];
  const schools = Array.isArray(schoolsData) ? schoolsData : [];

  const [searchTerm, setSearchTerm] = useState("");
  const [schoolId, setSchoolId] = useState("");

  const queryClient = useQueryClient();
  const pinMutation = useMutation({
    mutationFn: ({ eventId, pinned }: { eventId: string; pinned: boolean }) =>
      eventsAPI.setEventPinned(eventId, pinned),
    // A recurring event pins its whole series, so refetch rather than patch one row.
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.EVENTS }),
  });
  const isUpdating = (eventId: string) =>
    pinMutation.isPending && pinMutation.variables?.eventId === eventId;

  const bySchool = (event: Event) => !schoolId || handleSchoolId(event.school_id) === schoolId;

  const byDate = (a: Event, b: Event) =>
    new Date(a.date_and_time).getTime() - new Date(b.date_and_time).getTime();
  const term = searchTerm.trim().toLowerCase();

  const pinnedEvents = events.filter((event) => event.pinned && bySchool(event)).sort(byDate);
  const candidates = events
    .filter((event) => !event.pinned && bySchool(event))
    .filter((event) => !term || event.name.toLowerCase().includes(term))
    .sort(byDate);

  if (isLoading) {
    return (
      <DashboardLayout>
        <LoadingState type="table" />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <ErrorState
          title="Error loading events"
          message="Please try refreshing the page or log in again"
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="responsive-container">
        <div className="space-y-6">
          <PageHeader
            title="Pinned Events"
            description="Featured events are shown first in the app's events list and home, ahead of every other event on their campus."
          />

          <div className="flex justify-end">
            <SchoolFilter selectedSchoolId={schoolId} onSchoolChange={setSchoolId} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Pin className="h-4 w-4" />
                Pinned ({pinnedEvents.length})
              </CardTitle>
              <CardDescription>
                Pinning a recurring event keeps every upcoming occurrence pinned.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pinnedEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No pinned events yet. Pick one below.
                </p>
              ) : (
                <div className="flex gap-4 overflow-x-auto pb-2">
                  {pinnedEvents.map((event) => (
                    <div
                      key={event._id}
                      className="w-64 shrink-0 rounded-lg border border-border bg-card overflow-hidden"
                    >
                      <EventImage event={event} className="h-32 w-full" />
                      <div className="space-y-1 p-3">
                        <p className="font-medium truncate" title={event.name}>
                          {event.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(event.date_and_time)}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {getSchoolName(handleSchoolId(event.school_id), schools)}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 w-full"
                          disabled={isUpdating(event._id)}
                          onClick={() => pinMutation.mutate({ eventId: event._id, pinned: false })}
                        >
                          <PinOff className="h-4 w-4" />
                          Unpin
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Add an event</CardTitle>
              <CardDescription>Upcoming events that are not pinned.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search events by name..."
                  className="pl-9"
                />
              </div>

              {candidates.length === 0 ? (
                <p className="text-sm text-muted-foreground">No events match.</p>
              ) : (
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {candidates.map((event) => (
                    <li key={event._id} className="flex items-center gap-3 p-3">
                      <EventImage event={event} className="h-10 w-10 rounded-md" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{event.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {formatDateTime(event.date_and_time)} ·{" "}
                          {getSchoolName(handleSchoolId(event.school_id), schools)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        disabled={isUpdating(event._id)}
                        onClick={() => pinMutation.mutate({ eventId: event._id, pinned: true })}
                      >
                        <Pin className="h-4 w-4" />
                        Pin
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

function EventImage({ event, className }: { event: Event; className: string }) {
  if (!event.image_url) {
    return (
      <div className={`${className} bg-muted flex items-center justify-center`}>
        <Calendar className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }
  return <img src={event.image_url} alt={event.name} className={`${className} object-cover`} />;
}
